import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const THEME = {
  bg: '#1a1a1a',
  burgundy: '#800020',
  darkGreen: '#004d00',
  white: '#ffffff',
  card: '#262626',
  border: '#333333'
};

export default function AfricaLoudDashboard({ currentUser }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();

    const channel = supabase
      .channel('editorial-room')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'articles' }, () => fetchArticles())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  const fetchArticles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) { console.error('Supabase error:', error); setLoading(false); return; }
    console.log('Fetched articles:', data); // ? remove after confirming
    setArticles(data || []);
    setLoading(false);
  };

  const updateArticle = async (id, updates) => {
    const { error } = await supabase.from('articles').update(updates).eq('id', id);
    if (error) console.error('Update error:', error);
  };

  if (!currentUser) return null;

  return (
    <div style={{ ...styles.container, background: THEME.bg }}>
      <header style={styles.header}>
        <div>
          <h1 style={{ color: THEME.white, margin: 0 }}>Africa Loud</h1>
          <p style={{ color: THEME.burgundy, margin: '4px 0 0', letterSpacing: 2, fontSize: 12 }}>
            {currentUser.role?.toUpperCase()} DASHBOARD
          </p>
        </div>
        <div style={{ color: 'white', fontSize: 14 }}>{currentUser.displayName}</div>
      </header>

      {loading ? (
        <p style={{ color: '#666' }}>Loading articles...</p>
      ) : (
        <>
          <ArticlesTable articles={articles} />

          <main style={styles.grid}>
            {currentUser.role === 'writer' && (
              <WriterDashboard articles={articles} user={currentUser} onUpdate={updateArticle} />
            )}
            {currentUser.role === 'editor' && (
              <EditorDashboard articles={articles} user={currentUser} onUpdate={updateArticle} />
            )}
            {currentUser.role === 'director' && (
              <DirectorDashboard articles={articles} onUpdate={updateArticle} />
            )}
          </main>
        </>
      )}
    </div>
  );
}

const ArticlesTable = ({ articles }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ color: '#666', fontSize: 12, letterSpacing: 2, margin: '0 0 12px' }}>
      ARTICLES DATABASE ({articles.length})
    </h2>
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Writer</th>
            <th style={styles.th}>Editor</th>
            <th style={styles.th}>Link</th>
            <th style={styles.th}>Pages</th>
            <th style={styles.th}>Payment</th>
            <th style={styles.th}>Created</th>
          </tr>
        </thead>
        <tbody>
          {articles.length === 0 ? (
            <tr>
              <td colSpan={9} style={{ ...styles.td, color: '#555', textAlign: 'center', padding: 24 }}>
                No articles found in database
              </td>
            </tr>
          ) : (
            articles.map(a => (
              <tr key={a.id}>
                <td style={styles.td}>{a.title}</td>
                <td style={styles.td}>{a.category || '-'}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, background: statusColor(a.status) }}>
                    {a.status}
                  </span>
                </td>
                <td style={styles.td}>{a.writer_id || '-'}</td>
                <td style={styles.td}>{a.editor_id || '-'}</td>
                <td style={styles.td}>
                  {a.google_doc_link
                    ? <a href={a.google_doc_link} target="_blank" rel="noreferrer" style={{ color: '#800020' }}>Open</a>
                    : '-'}
                </td>
                <td style={styles.td}>{a.page_count ?? 0}</td>
                <td style={styles.td}>{a.payment_approved ? '? Yes' : 'No'}</td>
                <td style={styles.td}>{a.created_at?.split('T')[0]}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/** WRITER **/
const WriterDashboard = ({ articles, user, onUpdate }) => {
  const [docLinks, setDocLinks] = useState({});

  // Writer can only have 1 active article at a time
  const myActive = articles.filter(
    a => a.writer_id === user.id && ['Writing', 'Submitted'].includes(a.status)
  );
  const canClaim = myActive.length === 0;

  // Articles this writer can see:
  // 1. Available articles (to claim)
  // 2. Articles assigned to them
  const visible = articles.filter(
    a => a.status === 'Available' || a.writer_id === user.id
  );

  return (
    <>
      <h2 style={styles.sectionTitle}>Writer Panel</h2>

      {visible.length === 0 && (
        <p style={{ color: '#555', fontSize: 13 }}>No articles available right now.</p>
      )}

      {visible.map(art => (
        <div key={art.id} style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ color: 'white', margin: '0 0 4px', fontSize: 15 }}>{art.title}</h3>
              {art.category && <p style={{ color: '#666', margin: '0 0 8px', fontSize: 12 }}>{art.category}</p>}
            </div>
            <span style={{ ...styles.badge, background: statusColor(art.status) }}>{art.status}</span>
          </div>

          {/* Available ? Claim */}
          {art.status === 'Available' && (
            <button
              style={{ ...styles.btn, opacity: canClaim ? 1 : 0.4 }}
              disabled={!canClaim}
              title={!canClaim ? 'Finish your current article first' : ''}
              onClick={() => onUpdate(art.id, {
                writer_id: user.id,
                claimed_by: user.id,
                status: 'Writing'
              })}
            >
              {canClaim ? 'Claim Article' : 'Finish current article first'}
            </button>
          )}

          {/* Writing ? Submit with Google Doc link */}
          {art.writer_id === user.id && art.status === 'Writing' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input
                placeholder="Paste Google Doc link here"
                style={styles.input}
                value={docLinks[art.id] || ''}
                onChange={e => setDocLinks(prev => ({ ...prev, [art.id]: e.target.value }))}
              />
              <button
                style={styles.btn}
                disabled={!docLinks[art.id]}
                onClick={() => onUpdate(art.id, {
                  google_doc_link: docLinks[art.id],
                  status: 'Submitted',
                  writer_submitted: true
                })}
              >
                Submit
              </button>
            </div>
          )}

          {/* Submitted � waiting */}
          {art.writer_id === user.id && art.status === 'Submitted' && (
            <p style={{ color: '#4caf50', fontSize: 13, margin: '8px 0 0' }}>
              ? Submitted � awaiting editor
            </p>
          )}

          {/* Needs Polishing � writer must revise */}
          {art.writer_id === user.id && art.status === 'Needs Polishing' && (
            <>
              <p style={{ color: '#ff9800', fontSize: 13, margin: '8px 0 6px' }}>
                ? Sent back for polishing
              </p>
              {art.internal_notes && (
                <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 8px' }}>
                  Note: {art.internal_notes}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  placeholder="Updated Google Doc link"
                  style={styles.input}
                  value={docLinks[art.id] || art.google_doc_link || ''}
                  onChange={e => setDocLinks(prev => ({ ...prev, [art.id]: e.target.value }))}
                />
                <button
                  style={styles.btn}
                  onClick={() => onUpdate(art.id, {
                    google_doc_link: docLinks[art.id] || art.google_doc_link,
                    status: 'Submitted',
                    writer_submitted: true
                  })}
                >
                  Resubmit
                </button>
              </div>
            </>
          )}
        </div>
      ))}
    </>
  );
};

/** EDITOR **/
const EditorDashboard = ({ articles, user, onUpdate }) => {
  // Editor sees: Submitted articles (to claim) + their own articles
  const visible = articles.filter(
    a => a.status === 'Submitted' || a.editor_id === user.id
  );

  return (
    <>
      <h2 style={styles.sectionTitle}>Editor Panel</h2>

      {visible.length === 0 && (
        <p style={{ color: '#555', fontSize: 13 }}>No articles to edit right now.</p>
      )}

      {visible.map(art => (
        <div key={art.id} style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ color: 'white', margin: '0 0 4px', fontSize: 15 }}>{art.title}</h3>
              {art.category && <p style={{ color: '#666', margin: '0 0 8px', fontSize: 12 }}>{art.category}</p>}
            </div>
            <span style={{ ...styles.badge, background: statusColor(art.status) }}>{art.status}</span>
          </div>

          {art.google_doc_link && (
            <a href={art.google_doc_link} target="_blank" rel="noreferrer"
              style={{ color: '#800020', fontSize: 13, display: 'block', marginBottom: 10 }}>
              Open Google Doc ?
            </a>
          )}

          {/* Submitted + unclaimed ? claim it */}
          {art.status === 'Submitted' && !art.editor_id && (
            <button
              style={styles.btn}
              onClick={() => onUpdate(art.id, {
                editor_id: user.id,
                status: 'Editing',
                editor_received: true
              })}
            >
              Claim to Edit
            </button>
          )}

          {/* Submitted but claimed by someone else */}
          {art.status === 'Submitted' && art.editor_id && art.editor_id !== user.id && (
            <p style={{ color: '#555', fontSize: 13 }}>Claimed by another editor</p>
          )}

          {/* Editing ? send back or mark Under Review */}
          {art.editor_id === user.id && art.status === 'Editing' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                style={{ ...styles.btn, background: THEME.darkGreen }}
                onClick={() => onUpdate(art.id, { status: 'Under Review' })}
              >
                Send for Director Review
              </button>
              <button
                style={{ ...styles.btn, background: '#555' }}
                onClick={() => onUpdate(art.id, { status: 'Needs Polishing' })}
              >
                Send Back to Writer
              </button>
            </div>
          )}

          {/* Under Review � waiting on director */}
          {art.editor_id === user.id && art.status === 'Under Review' && (
            <p style={{ color: '#4caf50', fontSize: 13, margin: '8px 0 0' }}>
              ? Sent to director for review
            </p>
          )}

          {/* Approved */}
          {art.editor_id === user.id && art.status === 'Approved' && (
            <p style={{ color: '#4caf50', fontSize: 13, margin: '8px 0 0' }}>
              ? Approved by director
            </p>
          )}
        </div>
      ))}
    </>
  );
};

/** DIRECTOR **/
const DirectorDashboard = ({ articles, onUpdate }) => {
  const [notes, setNotes] = useState({});

  // Director sees everything except plain 'Available' articles with no activity
  const visible = articles.filter(a => a.status !== 'Available');

  return (
    <>
      <h2 style={styles.sectionTitle}>Director Control</h2>

      {visible.length === 0 && (
        <p style={{ color: '#555', fontSize: 13 }}>No articles in progress yet.</p>
      )}

      {visible.map(a => (
        <div key={a.id} style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ color: 'white', margin: '0 0 4px', fontSize: 15 }}>{a.title}</h3>
              {a.category && <p style={{ color: '#666', margin: '0 0 4px', fontSize: 12 }}>{a.category}</p>}
              <p style={{ color: '#555', fontSize: 12, margin: 0 }}>
                Writer: {a.writer_id || '�'} | Editor: {a.editor_id || '�'} | Pages: {a.page_count ?? 0}
              </p>
            </div>
            <span style={{ ...styles.badge, background: statusColor(a.status) }}>{a.status}</span>
          </div>

          {a.google_doc_link && (
            <a href={a.google_doc_link} target="_blank" rel="noreferrer"
              style={{ color: '#800020', fontSize: 13, display: 'block', margin: '10px 0' }}>
              Open Google Doc ?
            </a>
          )}

          {/* Under Review ? Approve or send back */}
          {a.status === 'Under Review' && (
            <>
              <input
                placeholder="Internal notes (optional)"
                style={{ ...styles.input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
                value={notes[a.id] || ''}
                onChange={e => setNotes(prev => ({ ...prev, [a.id]: e.target.value }))}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  style={{ ...styles.btn, background: THEME.darkGreen }}
                  onClick={() => onUpdate(a.id, {
                    status: 'Approved',
                    payment_approved: true,
                    internal_notes: notes[a.id] || ''
                  })}
                >
                  ? Approve & Authorize Payment
                </button>
                <button
                  style={{ ...styles.btn, background: '#555' }}
                  onClick={() => onUpdate(a.id, {
                    status: 'Needs Polishing',
                    internal_notes: notes[a.id] || ''
                  })}
                >
                  Send Back for Polishing
                </button>
              </div>
            </>
          )}

          {a.status === 'Approved' && (
            <p style={{ color: '#4caf50', fontSize: 13, margin: '10px 0 0' }}>
              ? Approved � payment authorized
            </p>
          )}

          {!['Under Review', 'Approved'].includes(a.status) && (
            <p style={{ color: '#555', fontSize: 12, marginTop: 8 }}>
              In progress � no action needed yet
            </p>
          )}
        </div>
      ))}
    </>
  );
};

// Helpers
const statusColor = (status) => {
  const map = {
    'Available':      '#1a3a1a',
    'Writing':        '#1a2a3a',
    'Submitted':      '#2a2a1a',
    'Editing':        '#2a1a3a',
    'Under Review':   '#3a2a1a',
    'Approved':       '#004d00',
    'Needs Polishing':'#3a1a1a',
  };
  return map[status] || '#333';
};

const styles = {
  container: { minHeight: '100vh', padding: 40, fontFamily: 'sans-serif' },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 30
  },
  grid: { display: 'grid', gap: 16 },
  card: {
    background: '#262626', padding: 20,
    borderRadius: 10, marginBottom: 12
  },
  sectionTitle: {
    color: '#666', fontSize: 12,
    letterSpacing: 2, margin: '0 0 16px'
  },
  table: { width: '100%', fontSize: 12, borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '8px 12px',
    borderBottom: '1px solid #333', color: '#888'
  },
  td: { padding: '8px 12px', borderBottom: '1px solid #222', color: '#ccc' },
  badge: {
    fontSize: 11, padding: '3px 8px',
    borderRadius: 20, color: 'white',
    whiteSpace: 'nowrap'
  },
  btn: {
    background: '#800020', color: 'white',
    border: 'none', borderRadius: 6,
    padding: '8px 16px', cursor: 'pointer',
    fontSize: 13, fontWeight: 600
  },
  input: {
    flex: 1, padding: '8px 12px',
    background: '#1a1a1a', border: '1px solid #333',
    color: 'white', borderRadius: 6,
    fontSize: 13, outline: 'none'
  }
};
